"use client";

import React from "react";
import { ArrowRight, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
const teamMembers = [{
  name: "Arthur Sterling",
  position: "Senior Managing Partner",
  bio: "With over three decades of experience in high-stakes litigation, Arthur Sterling founded the firm on the principles of unwavering integrity and architectural precision in legal strategy.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/arthur_sterling_legal.png",
  expertise: ["Corporate Litigation", "Strategic Advisory", "Estate Law"],
  projects: ["Global Tech Merger Defense", "Sterling Estate Restructuring", "Metropolitan Land Dispute"]
}, {
  name: "Eleanor Vance",
  position: "Head of Real Estate Law",
  bio: "Eleanor specializes in complex property acquisitions and urban development, bringing a meticulous eye for detail to every contract and negotiation.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/eleanor_vance_attorney.png",
  expertise: ["Commercial Real Estate", "Zoning Laws", "Contract Negotiation"],
  projects: ["Skyline Plaza Acquisition", "Industrial District Rezoning", "Luxury Residential Portfolio"]
}, {
  name: "Julian Thorne",
  position: "Senior Associate",
  bio: "Julian is known for his aggressive yet calculated approach to family law and private mediation, ensuring client interests are protected with absolute discretion.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/julian_thorne_lawyer.png",
  expertise: ["Family Law", "Private Mediation", "Asset Protection"],
  projects: ["High-Net-Worth Dissolution", "Private Trust Establishment", "Custody Mediation Series"]
}, {
  name: "Clara Beaumont",
  position: "Lead Counsel",
  bio: "Clara leads our corporate compliance division, navigating the intricate intersection of international law and domestic regulatory frameworks.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/clara_beaumont_counsel.png",
  expertise: ["Regulatory Compliance", "International Law", "Risk Management"],
  projects: ["Cross-Border Trade Compliance", "Financial Sector Audit", "Corporate Governance Reform"]
}];
export default function DistinguishedCounsel() {
  return <section data-section-id="228218" id="distinguished-counsel" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight mb-6">
              Distinguished <br />
              <span className="text-primary">Counsel</span>
            </h2>
            <p className="text-lg text-muted-foreground font-sans leading-relaxed">
              Our firm is anchored by a collective of elite legal minds, 
              dedicated to upholding the Sterling standard of excellence 
              through rigorous advocacy and sophisticated strategy.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
          {teamMembers.map((member, i) => <Sheet key={i}>
              <SheetTrigger asChild data-index={i}>
                <button className="group text-left w-full focus:outline-none">
                  <div className="relative aspect-[3/4] overflow-hidden bg-muted mb-6 rounded-md shadow-md">
                    <img src={member.image} alt={member.name} className="object-cover w-full h-full transition-all duration-700 ease-in-out group-hover:scale-105 grayscale group-hover:grayscale-0" />
                    
                    {}
                    <div className="absolute inset-0 bg-background/95 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 backdrop-blur-sm">
                      <span className="text-xs font-mono uppercase tracking-widest mb-4 text-primary block">
                        Practice Areas
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {member.expertise.map((exp, j) => <Badge key={j} variant="outline" className="border-primary/30 text-foreground bg-transparent font-sans rounded-md">
                            {exp}
                          </Badge>)}
                      </div>
                    </div>

                    {}
                    <div className="absolute top-4 right-4 size-10 rounded-md bg-primary/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                      <Plus className="size-5 text-primary-foreground" />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-default font-bold mb-1 group-hover:text-primary transition-colors">
                      {member.name}
                    </h3>
                    <p className="text-sm font-mono tracking-wide text-muted-foreground uppercase mb-3">
                      {member.position}
                    </p>
                    <span className="text-xs font-bold text-primary uppercase tracking-widest border-b border-primary/0 group-hover:border-primary/100 transition-all">
                      View Bio
                    </span>
                  </div>
                </button>
              </SheetTrigger>

              <SheetContent className="bg-background text-foreground border-border sm:max-w-md overflow-y-auto w-full rounded-md">
                <SheetHeader className="text-left mb-12 mt-8">
                  <SheetTitle className="text-4xl font-default font-bold tracking-tight mb-2">
                    {member.name}
                  </SheetTitle>
                  <SheetDescription className="text-lg text-primary font-mono tracking-wide uppercase">
                    {member.position}
                  </SheetDescription>
                </SheetHeader>

                <div className="space-y-12">
                  <section>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4 border-b border-border pb-2">
                      Professional Biography
                    </h4>
                    <p className="text-foreground/90 leading-relaxed font-sans text-lg">
                      {member.bio}
                    </p>
                  </section>

                  <section>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4 border-b border-border pb-2">
                      Core Expertise
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {member.expertise.map((exp, j) => <Badge key={j} variant="secondary" className="bg-secondary text-secondary-foreground font-sans text-sm py-1 rounded-md">
                          {exp}
                        </Badge>)}
                    </div>
                  </section>

                  <section>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4 border-b border-border pb-2">
                      Notable Case History
                    </h4>
                    <ul className="space-y-4">
                      {member.projects.map((project, j) => <li key={j} className="flex items-start gap-3 text-foreground font-sans group cursor-default">
                          <ArrowRight className="size-5 text-primary shrink-0 mt-0.5 group-hover:translate-x-1 transition-transform" />
                          <span className="text-lg group-hover:text-primary transition-colors">
                            {project}
                          </span>
                        </li>)}
                    </ul>
                  </section>
                </div>
              </SheetContent>
            </Sheet>)}
        </div>
      </div>
    </section>;
}